/**
 * Announcement marquee (askmario)
 * ---------------------------------------------------------------------------
 * A seamless, gap-free marquee. The section renders ONE set of announcement
 * items; this element measures that set against the container and clones it
 * enough times that each half of the track is at least as wide as the screen.
 * The CSS then animates translateX(0 -> -50%) — because the track is an even
 * number of identical sets, -50% lands on an identical frame, so it loops with
 * no blank gap and no jump, at any screen width or announcement count.
 */
class AnnouncementMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.announcement-bar__marquee-track');
    if (!this.track) return;

    // Snapshot the original single set (before we clone anything).
    this.originalItems = Array.from(this.track.children).map((node) => node.cloneNode(true));
    if (!this.originalItems.length) return;

    this.baseDuration = parseFloat(this.style.getPropertyValue('--announcement-marquee-duration')) || 40;

    this.build();
    this._onResize = this.debounce(this.build.bind(this), 200);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  build() {
    const track = this.track;

    // Reset to one clean set so we can measure it.
    track.classList.remove('is-ready');
    track.innerHTML = '';
    this.originalItems.forEach((node) => track.appendChild(node.cloneNode(true)));

    const setWidth = track.scrollWidth;
    const containerWidth = this.clientWidth;
    if (setWidth <= 0 || containerWidth <= 0) return;

    // Copies per half so each half is at least as wide as the container (no gap),
    // then two identical halves so the -50% loop is seamless.
    const perHalf = Math.max(1, Math.ceil(containerWidth / setWidth));
    const totalCopies = perHalf * 2;

    for (let i = 1; i < totalCopies; i += 1) {
      this.originalItems.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }

    // Keep the scroll speed the merchant set: -50% now spans `perHalf` sets, so
    // scale the duration to preserve px/second regardless of copies added.
    this.style.setProperty('--announcement-marquee-duration', this.baseDuration * perHalf + 's');

    // Restart the animation cleanly on the filled track.
    void track.offsetWidth;
    track.classList.add('is-ready');
  }

  debounce(fn, wait) {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }
}
customElements.define('announcement-marquee', AnnouncementMarquee);
